import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DocumentSnapshot } from 'firebase/firestore';
import { Product, ShopExtended } from '../../../domain/types';
import { catalogService } from '../../../domain/services/catalogService';
import { partnerService } from '../../../domain/services/partnerService';
import { useMarketing } from '../../../state/MarketingContext';
import { useFavorites } from '../../../state/FavoritesContext';
import { useAuth } from '../../../state/auth';
import { usePartner } from '../../../state/PartnerContext';
import { useDebounce } from '../../../hooks/useUtils';
import { logger } from '../../../infrastructure/logger';
import { PAGINATION, PRICING, DISPLAY_LIMITS, UI_ANIMATIONS } from '../../../domain/constants';

interface AdvancedFiltersState {
  priceMin: number;
  priceMax: number;
  conditions: string[];
  minRating: number | null;
  inStock: boolean;
  onSale: boolean;
}

interface UseCatalogDataReturn {
  // Data
  allProducts: Product[];
  shops: ShopExtended[];
  loading: boolean;
  loadingMore: boolean;
  loadingShops: boolean;
  hasMore: boolean;
  lastDoc: DocumentSnapshot | null;
  
  // Filters & Search
  filter: string;
  setFilter: (filter: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;
  sortBy: 'featured' | 'priceAsc' | 'priceDesc' | 'newest';
  setSortBy: (sort: 'featured' | 'priceAsc' | 'priceDesc' | 'newest') => void;
  selectedShop: string | null;
  setSelectedShop: (shopId: string | null) => void;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: (show: boolean) => void;
  advancedFilters: AdvancedFiltersState;
  
  // UI State
  stableSets: { featured: Product[]; impulse: Product[] };
  mainScrollRef: React.RefObject<HTMLDivElement | null>;
  filterScrollRef: React.RefObject<HTMLDivElement | null>;
  showLeftArrow: boolean;
  showRightArrow: boolean;
  
  // Computed
  isFavoritesView: boolean;
  hasFavorites: boolean;
  filterCategories: string[];
  totalItems: number;
  
  // Handlers
  fetchProducts: (isNextPage?: boolean) => Promise<void>;
  handleLoadMore: () => void;
  handleSelectFilter: (category: string) => void;
  scrollFilters: (direction: 'left' | 'right') => void;
  checkFilterScroll: () => void;
  handleFiltersChange: (filters: {
    priceRange?: { min?: number; max?: number } | null;
    condition?: string[] | null;
    rating?: number | null;
    inStock?: boolean;
    onSale?: boolean;
  }) => void;
}

export const useCatalogData = (): UseCatalogDataReturn => {
  const location = useLocation();
  const { categories } = useMarketing();
  const { favorites } = useFavorites();
  const { user } = useAuth();
  const { followingShops, followingShopIds } = usePartner();
  
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  
  const isFavoritesView = location.pathname === '/favorites';
  const hasFavorites = (favorites?.length || 0) > 0;

  // Filter Logic
  const [filter, setFilter] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, UI_ANIMATIONS.SEARCH_DEBOUNCE_MS);
  const [sortBy, setSortBy] = useState<'featured' | 'priceAsc' | 'priceDesc' | 'newest'>('featured');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedShop, setSelectedShop] = useState<string | null>(null);
  const [shops, setShops] = useState<ShopExtended[]>([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>({
    priceMin: 0,
    priceMax: PRICING.MAX_PRICE_FILTER,
    conditions: [],
    minRating: null,
    inStock: false,
    onSale: false
  });

  const [itemsPerPage] = useState(PAGINATION.PRODUCT_PAGE_SIZE);
  
  // Stable random sets for injections
  const [stableSets, setStableSets] = useState<{ featured: Product[]; impulse: Product[] }>({ 
    featured: [], 
    impulse: [] 
  });
  
  const mainScrollRef = useRef<HTMLDivElement | null>(null);
  const filterScrollRef = useRef<HTMLDivElement | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const isMounted = useRef(true);

  // Fetch shops for filter
  useEffect(() => {
    isMounted.current = true;
    const loadShops = async () => {
      if (!isMounted.current) return;
      setLoadingShops(true);
      try {
        const allShops = await partnerService.getActiveShops(PAGINATION.FILTERED_RESULTS_LIMIT);
        if (!isMounted.current) return;
        setShops(allShops);
      } catch (error) {
        if (!isMounted.current) return;
        logger.error('[useCatalogData] Error loading shops', error);
      } finally {
        if (isMounted.current) {
          setLoadingShops(false);
        }
      }
    };
    loadShops();
    return () => { isMounted.current = false; };
  }, []);

  // CORE FETCH FUNCTION
  const fetchProducts = useCallback(async (isNextPage: boolean = false) => {
    if (!isMounted.current) return;
    if (isFavoritesView && hasFavorites) {
      setLoading(false);
      return;
    }

    if (isNextPage) setLoadingMore(true);
    else setLoading(true);

    try {
      let result;
      const currentLastDoc = isNextPage ? lastDoc : null;

      if (filter === 'Todos') {
        result = await catalogService.getAllProducts(currentLastDoc, itemsPerPage);
      } else if (filter === 'Usados') {
        result = await catalogService.getUsedProducts(itemsPerPage);
      } else if (filter === 'Mis Tiendas') {
        const allProducts = await catalogService.getProductsByShopIds(followingShopIds, currentLastDoc, itemsPerPage);
        result = { products: allProducts, lastDoc: null, hasMore: false };
      } else {
        result = await catalogService.getProductsByCategory(filter, currentLastDoc, itemsPerPage);
      }

      if (!isMounted.current) return;

      if (result) {
        const validProducts = result.products.filter(p =>
          p.shopId &&
          p.shopId.trim() !== '' &&
          p.status === 'active' &&
          p.approvalStatus === 'active'
        );
        setAllProducts(prev => isNextPage ? [...prev, ...validProducts] : validProducts);
        setLastDoc(result.lastDoc);
        setHasMore(result.products.length === itemsPerPage);
        
        // Update stable sets only on first load
        if (!isNextPage) {
          setStableSets({
            featured: validProducts.filter(p => p.isFeatured),
            impulse: validProducts.filter(p => p.price < PRICING.IMPULSE_THRESHOLD)
          });
        }
      }
    } catch (error) {
      if (!isMounted.current) return;
      logger.error('[useCatalogData] Error fetching products', error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filter, isFavoritesView, hasFavorites, itemsPerPage, lastDoc, followingShopIds]);

  // Trigger fetch on filter change
  useEffect(() => {
    if (!isFavoritesView) {
      setAllProducts([]);
      setLastDoc(null);
      setHasMore(true);
      fetchProducts(false);
    }
  }, [filter, isFavoritesView, selectedShop, fetchProducts]);

  // Special Search Effect (debounced)
  useEffect(() => {
    if (debouncedSearchQuery.length > 1) {
      setLoading(true);
      catalogService.searchProducts(debouncedSearchQuery).then(res => {
        if (!isMounted.current) return;
        const validProducts = res.filter(p =>
          p.shopId &&
          p.shopId.trim() !== '' &&
          p.status === 'active' &&
          p.approvalStatus === 'active'
        );
        setAllProducts(validProducts);
        setHasMore(false);
        setLoading(false);
      });
    } else if (debouncedSearchQuery === '' && !loading) {
      fetchProducts(false);
    }
  }, [debouncedSearchQuery, loading, fetchProducts]);

  // Handle Deep Linking
  useEffect(() => {
    let targetCategory = '';
    if (location.state?.category) {
      targetCategory = location.state.category;
    } else {
      const params = new URLSearchParams(location.search);
      const catParam = params.get('category');
      if (catParam) targetCategory = catParam;
    }

    if (targetCategory) {
      setFilter(targetCategory);
      setSearchQuery('');
    }
  }, [location.state, location.search]);

  // Auto-Scroll Filter Bar
  useEffect(() => {
    if (filterScrollRef.current) {
      setTimeout(() => {
        const activeBtn = filterScrollRef.current?.querySelector(`button[data-category="${filter}"]`);
        if (activeBtn) activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 100);
    }
  }, [filter]);

  // Filter categories - Include "Mis Tiendas" if user is following shops
  const filterCategories = useMemo(() => {
    const cats = ['Todos', ...categories.filter(c => c.isActive).map(c => c.name)];
    if (user && followingShops.length > 0) {
      cats.push('Mis Tiendas');
    }
    return cats;
  }, [categories, user, followingShops.length]);

  const totalItems = allProducts.length;

  const checkFilterScroll = useCallback(() => {
    if (filterScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, []);

  useEffect(() => {
    checkFilterScroll();
    window.addEventListener('resize', checkFilterScroll);
    return () => window.removeEventListener('resize', checkFilterScroll);
  }, [checkFilterScroll]);

  useEffect(() => checkFilterScroll(), [categories, checkFilterScroll]);

  const scrollFilters = useCallback((direction: 'left' | 'right') => {
    if (filterScrollRef.current) {
      const amount = 200;
      filterScrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    }
  }, []);

  const handleSelectFilter = useCallback((category: string) => {
    setFilter(category);
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchProducts(true);
  }, [fetchProducts]);

  const handleFiltersChange = useCallback((filters: {
    priceRange?: { min?: number; max?: number } | null;
    condition?: string[] | null;
    rating?: number | null;
    inStock?: boolean;
    onSale?: boolean;
  }) => {
    setAdvancedFilters({
      priceMin: filters.priceRange?.min || 0,
      priceMax: filters.priceRange?.max || PRICING.MAX_PRICE_FILTER,
      conditions: filters.condition || [],
      minRating: filters.rating ?? null,
      inStock: filters.inStock || false,
      onSale: filters.onSale || false
    });
  }, []);

  return {
    // Data
    allProducts,
    shops,
    loading,
    loadingMore,
    loadingShops,
    hasMore,
    lastDoc,
    
    // Filters & Search
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    sortBy,
    setSortBy,
    selectedShop,
    setSelectedShop,
    showAdvancedFilters,
    setShowAdvancedFilters,
    advancedFilters,
    
    // UI State
    stableSets,
    mainScrollRef,
    filterScrollRef,
    showLeftArrow,
    showRightArrow,
    
    // Computed
    isFavoritesView,
    hasFavorites,
    filterCategories,
    totalItems,
    
    // Handlers
    fetchProducts,
    handleLoadMore,
    handleSelectFilter,
    scrollFilters,
    checkFilterScroll,
    handleFiltersChange
  };
};
